import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { posthog, getConsent, onConsentChange } from '../lib/posthog'

// Drives PostHog from inside the router + auth context:
//  - captures a $pageview on every client-side navigation (SPAs have no reload)
//  - identifies the user on login (so events tie to the account) and resets on
//    logout (so the next anonymous session isn't attributed to them)
// Renders nothing. All PostHog calls no-op until the user consents (PostHog is
// initialised opted-out), so nothing is captured before Accept; we re-run the
// identify effect when consent flips so a logged-in user is identified on Accept.
export function PostHogTracker() {
  const location = useLocation()
  const { session } = useAuth()
  // Track the last identified user so we only reset on an actual logout, not on
  // the initial anonymous render.
  const identifiedId = useRef<string | null>(null)
  // Re-run effects when the consent choice changes (e.g. Accept mid-session).
  const [consent, setConsent] = useState(getConsent())
  useEffect(() => onConsentChange(() => setConsent(getConsent())), [])

  useEffect(() => {
    // No-ops while opted out; captures once consent is granted. The page live at
    // the moment of Accept is captured by grantConsent(), so no double count.
    posthog.capture('$pageview')
  }, [location.pathname, location.search])

  useEffect(() => {
    if (consent !== 'granted') return // don't identify until consented
    const user = session?.user
    if (user) {
      if (identifiedId.current !== user.id) {
        posthog.identify(user.id, { email: user.email })
        identifiedId.current = user.id
      }
    } else if (identifiedId.current) {
      posthog.reset()
      identifiedId.current = null
    }
  }, [session, consent])

  return null
}
